import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ROUTES from 'src/constants/routes'
import { useAuth } from 'src/contexts/auth'
import { STORAGE_KEYS } from 'src/contexts/auth/constants'
import { useSendConfirmationCode } from 'src/hooks/api/user/mutations/useSendConfirmationCode'
import { useValidateConfirmationCode } from 'src/hooks/api/user/mutations/useValidateConfirmationCode'
import { useOtpInput } from 'src/components/OtpInput/hooks/useOtpInput'

export function usePhoneConfirmation() {
  const navigate = useNavigate()
  const { username, phoneRegistered } = useAuth()
  const otp = useOtpInput()

  const [codeSent, setCodeSent] = useState(false)

  const sendCodeMutation = useSendConfirmationCode()
  const validateCodeMutation = useValidateConfirmationCode()

  const isLoading = sendCodeMutation.isPending || validateCodeMutation.isPending

  useEffect(() => {
    if (!username) {
      navigate(ROUTES.AUTH.LOGIN)
    }
  }, [])

  const sendCode = useCallback(async () => {
    if (!username) return

    try {
      await sendCodeMutation.mutateAsync({ username, tipo: 'TELEFONE' })
      setCodeSent(true)
    } catch {
      toast.error('Erro ao enviar código. Tente novamente.')
    }
  }, [username, sendCodeMutation])

  const resendCode = useCallback(async () => {
    otp.resetCode()
    await sendCode()
  }, [otp, sendCode])

  const submitCode = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    if (!otp.isComplete) {
      toast.error('Por favor, informe o código completo de 6 dígitos')
      return
    }

    try {
      await validateCodeMutation.mutateAsync({
        username: username!,
        codigo: otp.codeString,
        tipo: 'TELEFONE'
      })

      toast.success('Telefone confirmado com sucesso!')
      localStorage.setItem(STORAGE_KEYS.PHONE_IS_CONFIRMED, 'True')

      setTimeout(() => {
        navigate(ROUTES.DASHBOARD)
      }, 1500)
    } catch {
      toast.error('Código inválido. Tente novamente.')
      otp.resetCode()
    }
  }, [username, otp, validateCodeMutation, navigate])

  return {
    otp,
    codeSent,
    isLoading,
    phoneRegistered,
    isSending: sendCodeMutation.isPending,
    sendCode,
    resendCode,
    submitCode
  }
}
